/**
 * MergeDownloadButton
 *
 * A download button that merges the selected builder slides into a single
 * .pptx file and triggers a client-side download.
 *
 * Disabled when the builder is empty. Shows a loading spinner during merge.
 * Uses sonner for toast feedback.
 *
 * When the backend rejects the merge because one or more slides have no
 * renderable PPTX blob (status 422 / 502, MergeUnmergeableError), the
 * button surfaces an AlertDialog listing the bad slides so the user can
 * remove them from the builder rather than silently shipping a corrupted
 * deck.
 */

import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { BuilderSlide } from "../types"

import { Download, Loader2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { downloadBlob } from "@/lib/download"

import { mergeSlides, MergeUnmergeableError } from "../api"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MergeDownloadButtonProps {
  slides: BuilderSlide[]
  /** Called with the API-reported slide IDs when the user accepts the
   *  "Remove from builder" action in the unmergeable dialog. */
  onRemoveSlides?: (slideIds: string[]) => void
  /** When the builder is editing a saved presentation, use its title as the
   *  download filename instead of the generic ``merged-N-slides.pptx``. */
  presentationName?: string | null
  /** Fired after a successful client-side download. */
  onDownloaded?: () => void
  /** Override the default `slide-builder-download` test id (e.g. dialog copy). */
  testId?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MergeDownloadButton({
  slides,
  onRemoveSlides,
  presentationName,
  onDownloaded,
  testId = "slide-builder-download",
}: MergeDownloadButtonProps) {
  const { t } = useTranslation()
  const [isMerging, setIsMerging] = useState(false)
  const [unmergeable, setUnmergeable] = useState<MergeUnmergeableError | null>(null)

  const handleMerge = async () => {
    if (slides.length === 0) return

    setIsMerging(true)
    try {
      const slideIds = slides.map((bs) => bs.slide.id)
      const trimmedName = presentationName?.trim().replace(/\.pptx$/i, "") || ""
      const filename = trimmedName ? `${trimmedName}.pptx` : `merged-${slides.length}-slides.pptx`
      const blob = await mergeSlides(slideIds, filename)
      downloadBlob(blob, filename)
      toast.success(t("slideSense.downloadedSlides", { count: slides.length }))
      onDownloaded?.()
    } catch (err) {
      if (err instanceof MergeUnmergeableError) {
        setUnmergeable(err)
      } else {
        console.error("Operation failed:", err)
        toast.error(t("slideSense.failedToMerge"))
      }
    } finally {
      setIsMerging(false)
    }
  }

  const handleRemoveFromBuilder = () => {
    if (unmergeable && onRemoveSlides) {
      onRemoveSlides(unmergeable.slideIds)
    }
    setUnmergeable(null)
  }

  const label = isMerging ? t("slideSense.merging") : t("slideSense.download")
  return (
    <>
      <Button
        aria-label={label}
        className="h-8 gap-1.5 px-3 text-xs"
        data-testid={testId}
        disabled={slides.length === 0 || isMerging}
        size="sm"
        title={t("slideSense.downloadLocalTooltip")}
        type="button"
        onClick={handleMerge}
      >
        {isMerging ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Download className="h-3.5 w-3.5" />
        )}
        {label}
      </Button>

      <AlertDialog
        open={unmergeable !== null}
        onOpenChange={(open) => !open && setUnmergeable(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.unmergeableTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("slideSense.unmergeableBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          {unmergeable && unmergeable.slideTitles.length > 0 && (
            <ul className="text-foreground/80 max-h-48 list-disc space-y-1 overflow-auto rounded-md border px-6 py-3 text-sm">
              {unmergeable.slideTitles.map((title, idx) => (
                <li key={`${unmergeable.slideIds[idx] ?? idx}`}>
                  {title || unmergeable.slideIds[idx]}
                </li>
              ))}
            </ul>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>{t("slideSense.unmergeableCancel")}</AlertDialogCancel>
            {onRemoveSlides && (
              <AlertDialogAction onClick={handleRemoveFromBuilder}>
                {t("slideSense.unmergeableRemove")}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
